<script lang="ts">
	import type { ItemsSub } from '$lib/tools/graph-db/create_map_state.svelte';
	import type { products, sellers } from '$lib/tools/graphdb';

	import Separator from '$lib/components/ui/separator/separator.svelte';
	import ButtonAsync from '$lib/utils/components/ButtonAsync.svelte';
	import Modal from '$lib/utils/components/Modal.svelte';
	import SelectSeller from './SelectSeller.svelte';
	import AddShipment from './AddShipment.svelte';

	import { create_cart_state } from '../../handlers/cart_state.svelte';
	import { utils } from '$lib/utils/utils.svelte';
	import { Loc } from '$lib/tools/locale.svelte';
	import { onMount } from 'svelte';

	let {
		open = $bindable(),
		mode,
		seller,
		products_sub_fn,
		sellers_sub_fn,
		cart_state = create_cart_state(mode === 'seller' ? seller : undefined)
	}: {
		open: boolean;
		mode: 'seller' | 'operator';
		seller?: sellers;
		cart_state?: ReturnType<typeof create_cart_state>;
		products_sub_fn(seller?: sellers): ItemsSub<products>;
		sellers_sub_fn?: () => ItemsSub<sellers>;
	} = $props();

	let sellers_list: ItemsSub<sellers> | undefined = $state();

	onMount(() => {
		if (!seller && sellers_sub_fn) sellers_list = sellers_sub_fn();
		return () => {
			cart_state.fns.destroy();
			if (sellers_list) sellers_list.unsubscribe();
		};
	});

	// If the seller is not set, but the seller has products, set the seller to the first seller with products
	$effect(() => {
		if (
			!seller &&
			cart_state.variables.seller &&
			!cart_state.variables.seller.Products?.length &&
			sellers_list?.values.some((s) => s.id === cart_state.variables.seller!.id)
		)
			cart_state.variables.seller = sellers_list?.values.find((s) => s.id === cart_state.variables.seller!.id);
	});
</script>

<Modal
	footer_snippet={cart_state?.variables.seller ? add_shipment_cart_snippet : undefined}
	card_props={{ content_classes: 'px-0' }}
	title="add|shipment"
	bind:open
>
	<div class="max-w-3xl mx-auto w-full">
		{#if mode === 'operator' && sellers_list && !cart_state.variables.seller}
			<SelectSeller {sellers_list} {cart_state} />
		{:else if cart_state.variables.seller}
			<AddShipment {mode} {products_sub_fn} bind:open {cart_state} />
		{/if}
	</div>
</Modal>

{#snippet add_shipment_cart_snippet()}
	{#if cart_state?.variables.seller}
		<div class="border-t w-full bg-background p-4 z-30">
			<div class="max-w-3xl mx-auto flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
				<div class="flex-1 space-y-1">
					<div class="flex items-center gap-4 text-sm text-muted-foreground">
						<div class="flex gap-1">
							<span>{Loc('products,the')}:</span>
							<span class="font-mono font-medium text-foreground">
								{utils.numbers.number_to_decimals(cart_state.calculations.products_total)}
							</span>
						</div>
						<Separator orientation="vertical" class="h-4" />
						<div class="flex gap-1">
							<span>{Loc('shipping,the')}:</span>
							<span
								class="font-mono font-medium text-foreground {cart_state.variables.is_free_shipping
									? 'line-through'
									: ''}"
							>
								{utils.numbers.number_to_decimals(cart_state.calculations.shipping_cost)}
							</span>
						</div>
					</div>

					<div class="flex items-baseline gap-2">
						<span class="text-xs font-bold uppercase text-muted-foreground tracking-wider"
							>{Loc('total|buyer,the')}</span
						>
						<div class="text-2xl font-black leading-none tracking-tight">
							{utils.numbers.number_to_decimals(cart_state.calculations.grand_total)}
							<span class="text-sm font-medium text-muted-foreground ml-1">{Loc('lyd')}</span>
						</div>
					</div>
				</div>

				<ButtonAsync
					size="lg"
					disabled={!cart_state.variables.is_ready_for_submit}
					onsuccess={() => (open = false)}
					onclick={cart_state.handle_submit}
					toasts={{
						success: Loc('success|add|shipment,the'),
						error: Loc('failed|add|shipment,the')
					}}
					class="w-full sm:w-auto min-w-[140px] font-bold shadow-sm"
				>
					{Loc('add|shipment,the')}
				</ButtonAsync>
			</div>
		</div>
	{/if}
{/snippet}
